'use client';
import { useMemo, useState, useSyncExternalStore } from 'react';
import type { DesktopPreviewState } from 'elowen-plugin-ui-kit';
import { PHONE_MAX_WIDTH } from '../../lib/breakpoints';

/** The complement of the phone breakpoint, also used by desktop.css to float chat tiles. A tile
 *  without a supplied still uses this boundary for its default collapsed framebuffer policy. */
const WIDE_QUERY = `(min-width: ${PHONE_MAX_WIDTH + 1}px)`;

const subscribeWide = (notify: () => void): (() => void) => {
  const query = window.matchMedia(WIDE_QUERY);
  query.addEventListener('change', notify);
  return () => query.removeEventListener('change', notify);
};
const readWide = (): boolean => window.matchMedia(WIDE_QUERY).matches;

/** Published as `hooks.useDesktopPreviewState`. A producer calls it once beside its `useDesktopControl` and
 *  hands the result to `DesktopPreview`. liveView is the default policy for a tile without a still;
 *  supplied stills poll while !expanded at every width. Plugin UI renders on the client only. */
export function useDesktopPreviewState(): DesktopPreviewState {
  const wide = useSyncExternalStore(subscribeWide, readWide, () => true);
  const [expanded, setExpanded] = useState(false);
  return useMemo(() => ({ expanded, setExpanded, wide, liveView: wide || expanded }), [expanded, wide]);
}
